import React, { useEffect, useState } from "react"
import gql from "graphql-tag"
import { useQuery } from "@apollo/client"
import { useParams } from "react-router-dom"
import { Popover, PopoverBody, Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Alert, Form, FormGroup, Label } from "reactstrap"
import Select from "react-select"
import withApolloClient from "../../withApolloClient"
import ConfirmationPopup from "../../Material/ConfirmationPopup"
import "./RenderEditColumnPopover.css"
import "./RenderAddCustomColumn.css"

const BoardColumnsQuery = gql`query BoardColumnsQuery($boardId: ID!) {
    allColumns(boardId: $boardId, isArchived: false) {
        edges {
            node {
                id
                title
                columnType
                contentType {
                    id
                    model
                }
            }
        }
    }
}`

const MirrorFieldOptionsQuery = gql`query MirrorFieldOptionsQuery($parentColumnId: ID!) {
    mirrorFieldOptions(parentColumnId: $parentColumnId) {
        name
        label
    }
}`

const UpdateBoardColumnMutation = gql`
    mutation UpdateBoardColumnMutation($input: UpdateBoardColumnMutationInput!){
        updateBoardColumn(input:$input){
            ok
            errors{
                field
                messages
            }
        }
    }
`

const CUSTOM_MIRROR_OPTION = "__CUSTOM__"

const selectMenuProps = {
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed",
    styles: {
        menuPortal: (base) => ({ ...base, zIndex: 2000 }),
    },
}

const buildOptionChanges = (initialOptions, nextOptions) => {
    const renamedOptions = {}
    const removedOptions = []
    const remainingOptions = [...nextOptions]

    initialOptions.forEach((originalOption) => {
        if (remainingOptions.includes(originalOption)) {
            remainingOptions.splice(remainingOptions.indexOf(originalOption), 1)
            return
        }

        if (remainingOptions.length) {
            const replacement = remainingOptions.shift()
            if (replacement && replacement !== originalOption) {
                renamedOptions[originalOption] = replacement
                return
            }
        }

        removedOptions.push(originalOption)
    })

    return { renamedOptions, removedOptions }
}

const RenderEditColumnPopover = ({ header, apolloClient, refetchBoardQuery, refetchTasksQuery }) => {
    const { boardId } = useParams()
    const [popoverOpen, setPopoverOpen] = useState(false)
    const [editOpen, setEditOpen] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [initialEnumOptions, setInitialEnumOptions] = useState([])
    const [mirrorSelectionInitialized, setMirrorSelectionInitialized] = useState(false)
    const [formData, setFormData] = useState({
        title: "",
        columnType: "",
        isRequired: false,
        parentColumnId: null,
        mirrorField: "",
        mirrorPath: "",
        customMirrorPath: "",
        allowedOptions: [],
    })

    const { data: boardColumnsData } = useQuery(BoardColumnsQuery, {
        variables: { boardId },
        skip: !boardId,
    })
    const { data: mirrorFieldOptionsData, loading: mirrorFieldOptionsLoading } = useQuery(MirrorFieldOptionsQuery, {
        variables: { parentColumnId: formData.parentColumnId },
        skip: !editOpen || formData.columnType !== "MIRROR" || !formData.parentColumnId,
    })

    const refetchColumnViews = async () => {
        if (refetchBoardQuery) {
            await refetchBoardQuery()
        }
        if (refetchTasksQuery) {
            await refetchTasksQuery()
        }
    }

    const resetEditState = () => {
        setEditOpen(false)
        setError("")
        setMirrorSelectionInitialized(false)
    }

    useEffect(() => {
        if (!editOpen) return

        const columnNode = header?.columnNode
        const mirrorPath = columnNode?.mirrorPath || ""
        const [modelName, ...pathParts] = mirrorPath.split(".")
        const pathRemainder = pathParts.join(".")
        const parentColumn = (boardColumnsData?.allColumns?.edges || [])
            .find(({ node }) => node.columnType === "FK_OBJECT" && node.contentType?.model === modelName)?.node

        setFormData({
            title: header?.title || "",
            columnType: columnNode?.columnType || "",
            isRequired: !!columnNode?.isRequired,
            parentColumnId: parentColumn?.id || null,
            mirrorField: "",
            mirrorPath: mirrorPath,
            customMirrorPath: pathRemainder,
            allowedOptions: [...(columnNode?.allowedOptions || [])],
        })
        setInitialEnumOptions([...(columnNode?.allowedOptions || [])])
        setError("")
        setMirrorSelectionInitialized(columnNode?.columnType !== "MIRROR")
    }, [editOpen, header, boardColumnsData])

    useEffect(() => {
        if (!editOpen || formData.columnType !== "MIRROR" || !formData.parentColumnId || mirrorSelectionInitialized === true) return
        if (mirrorFieldOptionsLoading || !mirrorFieldOptionsData) return

        const pathRemainder = formData.mirrorPath.split(".").slice(1).join(".")
        const matchingOption = (mirrorFieldOptionsData?.mirrorFieldOptions || []).find((option) => option.name === pathRemainder)

        setFormData((current) => ({
            ...current,
            mirrorField: matchingOption ? matchingOption.name : CUSTOM_MIRROR_OPTION,
            customMirrorPath: matchingOption ? "" : pathRemainder,
        }))
        setMirrorSelectionInitialized(true)
    }, [editOpen, formData.columnType, formData.parentColumnId, formData.mirrorPath, mirrorFieldOptionsData, mirrorFieldOptionsLoading, mirrorSelectionInitialized])

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target
        setFormData((current) => ({
            ...current,
            [name]: type === "checkbox" ? checked : value,
        }))
    }

    const handleSelectChange = (name, value) => {
        if (name === "parentColumnId") {
            setMirrorSelectionInitialized(false)
            setFormData((current) => ({
                ...current,
                parentColumnId: value,
                mirrorField: "",
                mirrorPath: "",
                customMirrorPath: "",
            }))
            return
        }

        if (name === "mirrorField") {
            const selectedParentColumn = (boardColumnsData?.allColumns?.edges || []).find((column) => column.node.id === formData.parentColumnId)?.node
            const modelName = selectedParentColumn?.contentType?.model || ""
            setFormData((current) => ({
                ...current,
                mirrorField: value,
                customMirrorPath: value === CUSTOM_MIRROR_OPTION ? current.customMirrorPath : "",
                mirrorPath: modelName && value && value !== CUSTOM_MIRROR_OPTION ? `${modelName}.${value}` : "",
            }))
            return
        }

        if (name === "customMirrorPath") {
            const selectedParentColumn = (boardColumnsData?.allColumns?.edges || []).find((column) => column.node.id === formData.parentColumnId)?.node
            const modelName = selectedParentColumn?.contentType?.model || ""
            const trimmedValue = value.trim()
            setFormData((current) => ({
                ...current,
                customMirrorPath: value,
                mirrorPath: modelName && trimmedValue ? `${modelName}.${trimmedValue}` : "",
            }))
        }
    }

    const handleAddEnumOption = () => {
        setFormData((current) => ({
            ...current,
            allowedOptions: [...(current.allowedOptions || []), ""],
        }))
    }

    const handleEnumOptionChange = (index, value) => {
        setFormData((current) => {
            const nextOptions = [...(current.allowedOptions || [])]
            nextOptions[index] = value
            return { ...current, allowedOptions: nextOptions }
        })
    }

    const handleRemoveEnumOption = (index) => {
        setFormData((current) => ({
            ...current,
            allowedOptions: (current.allowedOptions || []).filter((_, optionIndex) => optionIndex !== index),
        }))
    }

    const handleSave = async (e) => {
        e.preventDefault()
        if (!apolloClient) return setError("GQL Client not found")
        if (!header?.columnNode?.id) return setError("Column ID is required")

        const sanitizedTitle = formData.title.trim()
        const sanitizedAllowedOptions = (formData.allowedOptions || []).map((option) => option.trim()).filter(Boolean)
        const duplicateOption = sanitizedAllowedOptions.find((option, index) => sanitizedAllowedOptions.indexOf(option) !== index)

        if (!sanitizedTitle) return setError("Column name cannot be empty")
        if (formData.columnType === "ENUM" && !sanitizedAllowedOptions.length) return setError("At least one option is required")
        if (duplicateOption) return setError(`Option '${duplicateOption}' already exists`)
        if (formData.columnType === "MIRROR" && !formData.parentColumnId) return setError("Parent FK column is required")
        if (formData.columnType === "MIRROR" && !formData.mirrorField) return setError("Mirror field is required")
        if (formData.columnType === "MIRROR" && formData.mirrorField === CUSTOM_MIRROR_OPTION && !formData.customMirrorPath.trim()) {
            return setError("Custom mirror path is required")
        }

        setLoading(true)
        setError("")

        try {
            const input = {
                columnId: header.columnNode.id,
                title: sanitizedTitle,
                isRequired: formData.isRequired,
            }

            if (formData.columnType === "ENUM") {
                input.allowedOptions = sanitizedAllowedOptions
                input.optionChanges = JSON.stringify(buildOptionChanges(initialEnumOptions, sanitizedAllowedOptions))
            }

            if (formData.columnType === "MIRROR") {
                input.mirrorPath = formData.mirrorPath
            }

            const { data } = await apolloClient.mutate({
                mutation: UpdateBoardColumnMutation,
                variables: { input },
            })

            if (!data?.updateBoardColumn?.ok) {
                throw new Error(data?.updateBoardColumn?.errors?.[0]?.messages?.[0] || "Column update failed")
            }

            resetEditState()
            await refetchColumnViews()
        } catch (err) {
            setError(err.message || "Column update failed")
        } finally {
            setLoading(false)
        }
    }

    const handleArchive = async () => {
        setLoading(true)
        setError("")
        try {
            const { data } = await apolloClient.mutate({
                mutation: UpdateBoardColumnMutation,
                variables: { input: { columnId: header?.columnNode?.id, isArchived: true } },
            })

            if (!data?.updateBoardColumn?.ok) {
                throw new Error(data?.updateBoardColumn?.errors?.[0]?.messages?.[0] || "Column archive failed")
            }

            setConfirmOpen(false)
            setPopoverOpen(false)
            await refetchColumnViews()
        } catch (err) {
            setError(err.message || "Column archive failed")
        } finally {
            setLoading(false)
        }
    }

    const selectedParentColumn = (boardColumnsData?.allColumns?.edges || []).find((column) => column.node.id === formData.parentColumnId)?.node
    const mirrorFieldOptions = [
        ...(mirrorFieldOptionsData?.mirrorFieldOptions || []).map((option) => ({ value: option.name, label: option.label })),
        { value: CUSTOM_MIRROR_OPTION, label: "Custom path" },
    ]
    const mirrorFieldLabel = formData.mirrorField === CUSTOM_MIRROR_OPTION
        ? "Custom path"
        : (mirrorFieldOptionsData?.mirrorFieldOptions || []).find((option) => option.name === formData.mirrorField)?.label || formData.mirrorField

    return (
        <>
            <i
                id={`${header.id}Ellipsis`}
                title="More Options"
                className="fa fa-ellipsis-h task-ellipsis-icon"
                onClick={() => setPopoverOpen(!popoverOpen)}
            />

            <Popover
                trigger="legacy"
                className="task-header-menu-popover"
                placement="bottom"
                isOpen={popoverOpen}
                target={`${header.id}Ellipsis`}
                toggle={() => setPopoverOpen(false)}
            >
                <PopoverBody className="p-0">
                    <ul className="task-header-menu">
                        <li className="menu-item" onClick={() => {
                            setEditOpen(true)
                            setPopoverOpen(false)
                        }}>
                            <i className="fa fa-pencil-square-o" /> Edit
                        </li>
                        <li className="menu-item" onClick={() => {
                            setConfirmOpen(true)
                            setPopoverOpen(false)
                        }}>
                            <i className="fa fa-archive" /> Archive
                        </li>
                    </ul>
                </PopoverBody>
            </Popover>

            <Modal isOpen={editOpen} toggle={resetEditState} className="AddCustomColumnModal" centered>
                <ModalHeader>
                    <span className="add-custom-column-modal-title"><i className="fa fa-columns" aria-hidden="true" /> Edit Column</span>
                </ModalHeader>
                <ModalBody>
                    {error && <Alert color="danger">{error}</Alert>}
                    <Form onSubmit={handleSave} id="editColumnForm" className="addCustomColumnForm">
                        <div className="add-custom-column-intro">
                            Update this column's settings. You can rename it, mark it as required, and adjust enum or mirror settings without changing the column type.
                        </div>
                        <FormGroup className="add-custom-column-form-group">
                            <Label for="editColumnTitle">Column Name</Label>
                            <Input id="editColumnTitle" name="title" type="text" value={formData.title} onChange={handleChange} />
                        </FormGroup>
                        <FormGroup className="add-custom-column-form-group">
                            <Label for="editColumnType">Type</Label>
                            <Input id="editColumnType" type="text" value={formData.columnType} disabled />
                        </FormGroup>

                        {formData.columnType === "MIRROR" && (
                            <>
                                <Alert color="info" className="py-2">
                                    Mirror columns are read-only. You can change which FK column they mirror and which value path they display.
                                </Alert>
                                <FormGroup className="add-custom-column-form-group">
                                    <Label for="editMirrorParentColumnId">Parent FK Column</Label>
                                    <Select
                                        options={(boardColumnsData?.allColumns?.edges || [])
                                            .filter((column) => column.node.columnType === "FK_OBJECT")
                                            .map((column) => ({ value: column.node.id, label: column.node.title }))}
                                        name="parentColumnId"
                                        id="editMirrorParentColumnId"
                                        value={(boardColumnsData?.allColumns?.edges || [])
                                            .filter((column) => column.node.id === formData.parentColumnId)
                                            .map((column) => ({ value: column.node.id, label: column.node.title }))[0] || null}
                                        onChange={(value) => handleSelectChange("parentColumnId", value?.value || null)}
                                        {...selectMenuProps}
                                    />
                                </FormGroup>
                                <FormGroup className="add-custom-column-form-group">
                                    <Label for="editMirrorField">Mirror Value</Label>
                                    <Select
                                        options={mirrorFieldOptions}
                                        name="mirrorField"
                                        id="editMirrorField"
                                        value={formData.mirrorField ? { value: formData.mirrorField, label: mirrorFieldLabel } : null}
                                        onChange={(value) => handleSelectChange("mirrorField", value?.value || "")}
                                        isDisabled={!formData.parentColumnId}
                                        {...selectMenuProps}
                                    />
                                </FormGroup>
                                {formData.mirrorField === CUSTOM_MIRROR_OPTION && (
                                    <FormGroup className="add-custom-column-form-group">
                                        <Label for="editCustomMirrorPath">Custom Path</Label>
                                        <div className="input-group">
                                            <div className="input-group-prepend">
                                                <span className="input-group-text">{selectedParentColumn?.contentType?.model || ""}.</span>
                                            </div>
                                            <Input
                                                id="editCustomMirrorPath"
                                                type="text"
                                                value={formData.customMirrorPath}
                                                onChange={(e) => handleSelectChange("customMirrorPath", e.target.value)}
                                                placeholder="current_agreement.start_date"
                                                disabled={!formData.parentColumnId}
                                            />
                                        </div>
                                    </FormGroup>
                                )}
                            </>
                        )}

                        {formData.columnType === "ENUM" && (
                            <FormGroup className="add-custom-column-form-group">
                                <Label>Options</Label>
                                {(formData.allowedOptions || []).map((opt, index) => (
                                    <div key={`${header.id}-enum-${index}`} className="input-group mb-1">
                                        <Input
                                            type="text"
                                            value={opt}
                                            onChange={(e) => handleEnumOptionChange(index, e.target.value)}
                                            placeholder={`Option ${index + 1}`}
                                        />
                                        <Button color="danger" size="sm" type="button" onClick={() => handleRemoveEnumOption(index)} disabled={loading}>×</Button>
                                    </div>
                                ))}
                                <Button color="secondary" size="sm" type="button" onClick={handleAddEnumOption} style={{ width: "100%" }} disabled={loading}>
                                    + Add Option
                                </Button>
                            </FormGroup>
                        )}

                        <FormGroup check className="add-custom-column-required">
                            <Input type="checkbox" name="isRequired" id={`isRequired-${header.id}`} checked={formData.isRequired} onChange={handleChange} />
                            <Label for={`isRequired-${header.id}`}>Require this field on tasks</Label>
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter className="add-custom-column-modal-footer">
                    <Button color="secondary" type="button" onClick={resetEditState} disabled={loading}>Cancel</Button>
                    <Button color="primary" type="submit" form="editColumnForm" disabled={loading}>
                        {loading ? "Saving..." : "Save Changes"}
                    </Button>
                </ModalFooter>
            </Modal>

            <ConfirmationPopup
                open={confirmOpen}
                message="Are you sure you want to archive this column?"
                loading={loading}
                handleClose={() => {
                    setConfirmOpen(false)
                    setError("")
                }}
                action="Yes"
                confirmAction={handleArchive}
                color="danger"
                error={error}
            />
        </>
    )
}

export default withApolloClient(RenderEditColumnPopover)
